import type { Bar, Pivot } from '../types';

export type StructureLabel = NonNullable<Pivot['label']>;
export interface StructurePoint { label: StructureLabel; price: number; pivotTime: number; confirmedAt: number }

// Match the NinjaScript overlay: strength three, strict neighboring comparisons,
// no minimum-movement filter, and both high and low when one bar qualifies as both.
function connectorPivots(bars: Bar[]): Array<Pivot & { label: StructureLabel }> {
  const strength = 3;
  const pivots: Array<Pivot & { label: StructureLabel }> = [];
  const previous: Partial<Record<'high' | 'low', number>> = {};
  for (let index = strength; index < bars.length - strength; index++) {
    const bar = bars[index];
    const neighbors = bars.slice(index - strength, index + strength + 1);
    for (const kind of ['high', 'low'] as const) {
      const price = kind === 'high' ? bar.high : bar.low;
      const qualifies = neighbors.every((neighbor, offset) => offset === strength ||
        (kind === 'high' ? price > neighbor.high : price < neighbor.low));
      if (!qualifies) continue;
      const earlier = previous[kind];
      previous[kind] = price;
      if (earlier === undefined) continue;
      const label: StructureLabel = kind === 'high' ? (price > earlier ? 'HH' : 'LH') : (price > earlier ? 'HL' : 'LL');
      pivots.push({ kind, index, time: bar.time, price, label, confirmedAt: bars[index + strength].time });
    }
  }
  return pivots;
}

export function recentStructure(bars: Bar[], count = 15): StructurePoint[] {
  return connectorPivots(bars)
    .slice(-count)
    .map(pivot => ({ label: pivot.label, price: pivot.price, pivotTime: pivot.time, confirmedAt: pivot.confirmedAt }));
}

export function structureRows(bars: Bar[], count = 15) {
  const pivots = connectorPivots(bars);
  const points = (kind: 'high' | 'low'): StructurePoint[] => pivots.filter(pivot => pivot.kind === kind)
    .slice(-count).map(pivot => ({ label: pivot.label, price: pivot.price, pivotTime: pivot.time, confirmedAt: pivot.confirmedAt }));
  return { highs: points('high'), lows: points('low') };
}

export function describeStructure(points: StructurePoint[]) {
  const labels = new Set(points.map(point => point.label));
  if (points.length < 2) return 'Awaiting confirmed connectors';
  if ([...labels].every(label => label === 'HH' || label === 'HL')) return 'Bullish channel';
  if ([...labels].every(label => label === 'LH' || label === 'LL')) return 'Bearish channel';
  return 'Mixed / transitioning';
}

export function structureBias(rows: ReturnType<typeof structureRows>, currentPrice?: number): 'bullish' | 'bearish' | 'mixed' | 'insufficient' {
  const highs = rows.highs.slice(-2), lows = rows.lows.slice(-2);
  if (highs.length < 2 || lows.length < 2) return 'insufficient';
  if (highs.every(point => point.label === 'HH') && lows.every(point => point.label === 'HL'))
    return currentPrice != null && currentPrice < lows.at(-1)!.price - 1 ? 'mixed' : 'bullish';
  if (highs.every(point => point.label === 'LH') && lows.every(point => point.label === 'LL'))
    return currentPrice != null && currentPrice > highs.at(-1)!.price + 1 ? 'mixed' : 'bearish';
  return 'mixed';
}
